import { z } from "zod";

/**
 * URL search params for `/$workspaceSlug/payroll/tax-calculator`.
 *
 * KEYS ONLY, never values (FE-SEC7). The calculator's inputs are a salary, a
 * rent and an age band, and none of them belong in a URL: the address bar is
 * the one place a figure survives into browser history, a referrer header and
 * somebody else's screen share. Those travel in POST bodies. What is here is
 * a period id (opaque), a view name (fixed vocabulary) and a regime key (the
 * pack's vocabulary), all of which are safe to bookmark and send.
 */

/// `compare` is the read-only side by side; `optimise` is the proposal view.
export const TAX_CALCULATOR_VIEWS = ["compare", "optimise"] as const;
export type TaxCalculatorView = (typeof TAX_CALCULATOR_VIEWS)[number];

export const taxCalculatorRouteSearchSchema = z
  .object({
    /// Which tax year is being calculated. An opaque id, so it carries nothing
    /// about the person.
    period: z.string().optional(),
    /// Plain `z.string()` rather than an enum, for the reason the sibling
    /// search schemas give at length: `.catch` would rewrite the parsed value
    /// while leaving the URL saying something else, so the address bar and the
    /// page would disagree for the rest of the session. The component narrows.
    view: z.string().optional(),
    /// A pack regime key (`new`, `old`, `old_senior`, ...). Not an enum here
    /// either: the pack is the vocabulary (C-06), so a key this build has not
    /// heard of must still round-trip rather than be silently rewritten.
    regime: z.string().optional(),
  })
  .strict();

export type TaxCalculatorRouteSearch = z.infer<typeof taxCalculatorRouteSearchSchema>;

/// Narrow the free-form `view` param to the two the page renders, defaulting
/// to the comparison. Done here rather than in the schema so the URL keeps
/// whatever it said.
export function viewOf(search: TaxCalculatorRouteSearch): TaxCalculatorView {
  return search.view === "optimise" ? "optimise" : "compare";
}
